/* eslint-disable no-console */
import { CloseOutlined, ExclamationCircleFilled, QuestionCircleOutlined, StarFilled } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import {
  Avatar,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Progress,
  Radio,
  Rate,
  Select,
  Steps,
  Tooltip,
  Typography,
  message,
} from 'antd'
import axios from 'axios'
import { useAtom } from 'jotai'
import { uniqBy } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { UserAvatar, UsersSearch } from '@/components'
import { departmentsMapAtom } from '@/modules/department'
import { DevlopStatus } from '@/modules/devlop/devlop.schema'
import { User, usersAtom } from '@/modules/user'
import { routes } from '@/router'
import { ProjectStatus, Resource, ResourceEvaluation, ResourceType, ResourceUser } from '@/schemas'

interface ResourceEvaluationFormProps extends ModalProps {
  resource: Resource
  onRefresh: () => void
}

const ResourceEvaluationForm: React.FC<ResourceEvaluationFormProps> = React.forwardRef(
  ({ resource, onRefresh }, ref) => {
    const { t } = useTranslation()
    const history = useHistory()
    const [form] = Form.useForm()

    React.useImperativeHandle(ref, () => ({
      submit: async () => {
        await form
          .validateFields()
          .then((values) => {
            run(values)
          })
          .catch((err) => console.error(err))
      },
      check: async () => {
        let res = false
        await form
          .validateFields()
          .then(() => {
            res = true
          })
          .catch(() => {})
        return res
      },
    }))

    const { run } = useRequest(
      (data) => ({
        url: '/api/evaluation/createResourceEvaluation',
        method: 'post',
        data: {
          ...data,
          highlightMembers: data.highlightMembers || [],
          meetingUsers: data.meetingUsers.filter((u: string) => u !== resource.email),
          selfId: resource.selfId,
          resourceType: resource.type,
          status: 0,
        },
      }),
      {
        manual: true,
        onSuccess: async () => {
          console.log(1, 'create onSuccess')
          switch (resource.type) {
            case ResourceType.PROJECT:
              await axios.patch('/api/project/updateProject', {
                selfId: resource.selfId,
                status: ProjectStatus.已结项,
              })
              break
            case ResourceType.CUSTOMER:
              await axios.post('/api/customer/flow/action', {
                selfId: resource.selfId,
                flowStatus: resource['flowStatus'],
              })
              break
            case ResourceType.PRODUCTION:
              await axios.post('/api/production/flow/action', {
                selfId: resource.selfId,
                flowStatus: resource['flowStatus'],
              })
              break
            case ResourceType.DEVLOP:
              await axios.patch(`/api/devlop/patch/${resource.selfId}`, {
                status: DevlopStatus.已结项,
              })
              break
          }
          console.log(2, 'create onSuccess, review')
          onRefresh()
          console.log(3, 'create onSuccess, refresh')
          switch (resource.type) {
            case ResourceType.PROJECT:
              history.push(routes.project.page(resource.selfId, { tab: 'finish' }))
              break
            case ResourceType.CUSTOMER:
              history.push(routes.customer.detail(resource.selfId, { tab: 'finish' }))
              break
            case ResourceType.PRODUCTION:
              history.push(routes.production.detail(resource.selfId, { tab: 'finish' }))
              break
            case ResourceType.DEVLOP:
              history.push(routes.devlop.detail(resource.selfId, { tab: 'finish' }))
              break
          }
          console.log(4, 'create onSuccess, history')
        },
      }
    )

    return (
      <Form form={form} labelCol={{ span: 6 }}>
        <Form.Item label={t('evaluation.completion')} name='completion' rules={[{ required: true }]}>
          <InputNumber style={{ width: 120 }} min={0} max={100} placeholder='0 - 100' />
        </Form.Item>
        <Form.Item label={t('evaluation.satisfaction')} name='satisfaction' rules={[{ required: true }]}>
          <Radio.Group>
            <Radio value='不满意'>不满意</Radio>
            <Radio value='一般'>一般</Radio>
            <Radio value='满意'>满意</Radio>
            <Radio value='非常满意'>非常满意</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item label={t('evaluation.delayRatio')} name='delayRatio' rules={[{ required: true }]}>
          <Select
            options={[
              { label: '延期', value: 1 },
              { label: '无延期', value: 0 },
            ]}
          />
        </Form.Item>

        <Form.Item label={t('evaluation.highlightMembers')} name='highlightMembers'>
          <UsersSearch mode='multiple' />
        </Form.Item>

        <Form.Item label={t('evaluation.meetingUsers')} name='meetingUsers' rules={[{ required: true }]}>
          <UsersSearch mode='multiple' />
        </Form.Item>
      </Form>
    )
  }
)

interface ResourceEvaluationInviteProps extends ModalProps {
  selfId: string
  resourceType: ResourceType
  canUpdate: boolean
  initValue?: {
    deadline?: moment.Moment
    invitedUsers: ResourceEvaluation[]
    users: ResourceEvaluation[]
  }
  onRefresh: () => void
}

export const ResourceEvaluationInvitationForm: React.FC<ResourceEvaluationInviteProps> = ({
  selfId,
  resourceType,
  canUpdate,
  initValue,
  onRefresh,
  ...props
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()

  const { loading, run } = useRequest(
    (data) => ({
      url: '/api/evaluation/inviteResourceEvaluations',
      method: 'post',
      data: {
        selfId,
        resourceType,
        users: data.newUsers.filter((email: string) => !initValue?.users.map((u) => u.email).includes(email)),
        deadline: moment(data.deadline).format('YYYY-MM-DD'),
      },
    }),
    {
      manual: true,
      onSuccess: async () => {
        message.success(t('invite success'))
        onRefresh()
        props.onCancel?.(null as any)
        form.resetFields()
      },
    }
  )

  const { run: runAgain } = useRequest(
    () => ({
      url: '/api/evaluation/inviteResourceEvaluationsAgain',
      method: 'post',
      data: {
        selfId,
        resourceType,
        users: initValue?.invitedUsers || [],
      },
    }),
    {
      manual: true,
      onSuccess: async () => {
        message.success(t('invite success'))
        onRefresh()
        props.onCancel?.(null as any)
      },
    }
  )

  const onOk = async () => {
    await form
      .validateFields()
      .then((values) => {
        run(values)
      })
      .catch((err) => console.error(err))
  }
  return (
    <Modal
      maskClosable={false}
      destroyOnClose
      title={t('evaluation.invite')}
      okText={t('evaluation.sendInvite')}
      okButtonProps={{ loading }}
      onOk={onOk}
      {...props}
    >
      <Form form={form} labelCol={{ span: 6 }} initialValues={initValue}>
        <Form.Item label={t('evaluation.deadline')} name='deadline' rules={[{ required: true }]}>
          <DatePicker style={{ width: 110 }} size='small' suffixIcon={null} />
        </Form.Item>

        <Form.Item label={t('evaluation.invited')}>
          <Avatar.Group maxCount={5}>
            {initValue?.invitedUsers.map((o) => (
              <UserAvatar key={o.email} email={o.email} />
            ))}
          </Avatar.Group>
          {canUpdate && initValue?.invitedUsers.length && (
            <Button size='small' type='link' className='ml-5px -top-2.5' onClick={() => runAgain()}>
              {t('evaluation.inviteAgain')}
            </Button>
          )}
        </Form.Item>

        <Form.Item label={t('evaluation.newinvitor')} name='newUsers' rules={[{ required: true }]}>
          <UsersSearch mode='multiple' />
        </Form.Item>
      </Form>
    </Modal>
  )
}

type ResourceUserEvaluationProps = ResourceEvaluationFormProps

interface UserEvaluationObj {
  [key: string]: {
    achievement: {
      score: number
      description: string
    }
    efficiency: {
      score: number
      description: string
    }
    cooperation: {
      score: number
      description: string
    }
  }
}

export const ResourceUserEvaluationModal: React.FC<ResourceUserEvaluationProps> = ({
  resource,
  onRefresh,
  ...props
}) => {
  const { t } = useTranslation()
  const [current, setCurrent] = React.useState(0)
  const [hasSkip, setHasSkip] = React.useState(false)
  const [confirmVisible, setConfirmVisible] = React.useState(false)
  const [confirmCheckBox, setConfirmCheckBox] = React.useState(false)
  const [currentEmail, setCurrentEmail] = React.useState('')

  const [userEvaluationObj, setUserEvaluationObj] = React.useState<UserEvaluationObj>({})

  const resourceEvaluationRef = React.useRef<{ submit: any; check: any }>()

  const { data: users } = useRequest<ResourceUser[]>(
    {
      url: '/api/userRoles/getResourceUsers',
      params: {
        selfId: [resource.selfId],
        type: resource.type,
      },
    },
    {
      refreshDeps: [props.visible],
    }
  )

  const { run: createUserEvaluation } = useRequest(
    (data) => ({
      url: '/api/evaluation/createUserEvaluation',
      method: 'post',
      data: {
        selfId: resource.selfId,
        content: data,
        resourceType: resource.type,
      },
    }),
    {
      manual: true,
      onSuccess: async () => {
        setCurrent(1)
      },
    }
  )

  if (!users) return null

  const selectUser = (email: string | string[]) => {
    const _ = JSON.parse(JSON.stringify(userEvaluationObj))
    if (Array.isArray(email)) {
      email.forEach((e) => {
        if (!userEvaluationObj[e]) {
          _[e] = {
            achievement: {
              score: 0,
              description: '',
            },
            efficiency: {
              score: 0,
              description: '',
            },
            cooperation: {
              score: 0,
              description: '',
            },
          }
        }
      })
      setCurrentEmail(email[0])
    } else {
      if (!userEvaluationObj[email]) {
        _[email] = {
          achievement: {
            score: 0,
            description: '',
          },
          efficiency: {
            score: 0,
            description: '',
          },
          cooperation: {
            score: 0,
            description: '',
          },
        }
      }
      setCurrentEmail(email)
    }
    setUserEvaluationObj(_)
  }

  const removeUser = (email: string) => {
    const _ = JSON.parse(JSON.stringify(userEvaluationObj))
    delete _[email]
    if (Object.keys(_).length) {
      setCurrentEmail(Object.keys(_)[0])
    } else {
      setCurrentEmail('')
    }
    setUserEvaluationObj(_)
  }

  const checkProgress = (showMessage = true) => {
    const emails = Object.keys(userEvaluationObj)
    for (let i = 0; i < emails.length; i++) {
      const email = emails[i]
      if (calProgress(email, userEvaluationObj) !== 100) {
        showMessage && message.error(`${email} 未填写完成`)
        return false
      }
    }
    return true
  }

  const footer = (
    <div className='text-center'>
      {current === 0 && (
        <Button
          type='ghost'
          onClick={() => {
            setHasSkip(true)
            setCurrent(1)
          }}
        >
          跳过
        </Button>
      )}
      {current === 0 && (
        <Button
          type='primary'
          disabled={!Object.keys(userEvaluationObj).length || !checkProgress(false)}
          onClick={() => {
            setHasSkip(false)
            createUserEvaluation(userEvaluationObj)
          }}
        >
          下一步
        </Button>
      )}
      {current > 0 && (
        <Button style={{ marginLeft: 8 }} onClick={() => setCurrent(0)}>
          上一步
        </Button>
      )}
      {current === 1 && (
        <Button
          type='primary'
          onClick={async () => {
            if (!(await resourceEvaluationRef.current?.check())) return
            if (!hasSkip) checkProgress() && setConfirmVisible(true)
            if (hasSkip) setConfirmVisible(true)
          }}
        >
          确认
        </Button>
      )}
    </div>
  )

  const confirm = async () => {
    setConfirmCheckBox(false)
    setConfirmVisible(false)
    await resourceEvaluationRef.current?.submit()
    console.log(5, 'submit ')
    props.onCancel?.(null as any)
    console.log(5, 'submit cancel')
  }

  return (
    <Modal maskClosable={false} destroyOnClose title={t('finished title')} footer={footer} {...props}>
      <Steps current={current} onChange={(value) => setCurrent(value)} className='mb-20px'>
        <Steps.Step title={t('resource user evaluation')} />
        <Steps.Step title={t('resource evaluation')} />
      </Steps>
      {current === 0 ? (
        <div className='flex'>
          <div className='space-y-20px flex-1 mr-20px'>
            {currentEmail && (
              <UserEvaluationCard email={currentEmail} value={userEvaluationObj} setValue={setUserEvaluationObj} />
            )}
          </div>
          <div className=' flex-shrink-0 w-300px'>
            <UserSelectList
              selectedUser={currentEmail}
              selectUser={selectUser}
              removeUser={removeUser}
              value={userEvaluationObj}
              hasSkip={hasSkip}
              projectUsers={users}
            />
          </div>
        </div>
      ) : (
        <ResourceEvaluationForm resource={resource} onRefresh={onRefresh} ref={resourceEvaluationRef} />
      )}
      <Modal
        visible={confirmVisible}
        okText={'确认提交评价'}
        onOk={confirm}
        okButtonProps={{ disabled: !confirmCheckBox }}
        onCancel={() => {
          setConfirmCheckBox(false)
          setConfirmVisible(false)
        }}
        centered
        closable={false}
        cancelText={'再想想'}
        cancelButtonProps={{
          type: 'text',
          className: 'text-typography-body',
        }}
      >
        <div className='flex '>
          <ExclamationCircleFilled className='mt-0.5 text-xl mr-5 text-[#FAAD15]' />
          <div>
            <p className='text-xl mb-3'>
              提交后<span className='text-red-500'>无法进行修改人员评价</span>，确认提交吗？
            </p>
            <p className='text-typography-header mb-3'>如需提交评价，请勾选确认以下内容</p>
            <div className='border-solid bg-[#FFFBE6] border-1 border-[#FFD472] p-2'>
              <div>
                <Checkbox
                  className='mr-2'
                  checked={confirmCheckBox}
                  onChange={(e) => setConfirmCheckBox(e.target.checked)}
                />
                <span className=' cursor-pointer' onClick={() => setConfirmCheckBox(!confirmCheckBox)}>
                  我已知晓提交后无法进行修改，继续提交。
                </span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </Modal>
  )
}

const UserEvaluationCard: React.FC<{ email: string; value: UserEvaluationObj; setValue: any }> = ({
  email,
  value,
  setValue,
}) => {
  const setCardValue = (key: string, str: string, _value: number | string) => {
    const _ = JSON.parse(JSON.stringify(value))
    _[email][key][str] = _value
    setValue(_)
  }
  return (
    <>
      <div>
        <div>
          交付成果
          <Tooltip title='对交付的内容满意度（质量，性能，接口易用程度，效果等）'>
            <QuestionCircleOutlined className='ml-4px' />
          </Tooltip>
        </div>
        <div className='flex'>
          <RateCard
            value={value[email].achievement.score}
            type={1}
            onChange={(e: number) => setCardValue('achievement', 'score', e)}
          />
          <Input.TextArea
            className='ml-20px'
            value={value[email].achievement.description}
            placeholder='请给出打分的说明和理由（必填）'
            onChange={(e) => setCardValue('achievement', 'description', e.target.value)}
          />
        </div>
      </div>
      <div>
        <div>
          交付效率
          <Tooltip title='对承诺的交付时间满意度（如果项目有变更，且至少提早2周左右通知）'>
            <QuestionCircleOutlined className='ml-4px' />
          </Tooltip>
        </div>
        <div className='flex'>
          <RateCard
            value={value[email].cooperation.score}
            type={1}
            onChange={(e: number) => setCardValue('cooperation', 'score', e)}
          />
          <Input.TextArea
            className='ml-20px'
            value={value[email].cooperation.description}
            placeholder='请给出打分的说明和理由（必填）'
            onChange={(e) => setCardValue('cooperation', 'description', e.target.value)}
          />
        </div>
      </div>
      <div>
        <div>
          协作性
          <Tooltip title='与其交流，沟通协作的顺畅程度，可以完成整体的任务和目标'>
            <QuestionCircleOutlined className='ml-4px' />
          </Tooltip>
        </div>
        <div className='flex'>
          <RateCard
            value={value[email].efficiency.score}
            type={2}
            onChange={(e: number) => setCardValue('efficiency', 'score', e)}
          />
          <Input.TextArea
            className='ml-20px'
            value={value[email].efficiency.description}
            placeholder='请给出打分的说明和理由（必填）'
            onChange={(e) => setCardValue('efficiency', 'description', e.target.value)}
          />
        </div>
      </div>
    </>
  )
}

const calProgress = (email: string, value: UserEvaluationObj) => {
  let finishedCount = 0
  if (value[email].achievement.score) finishedCount++
  if (value[email].achievement.description) finishedCount++
  if (value[email].cooperation.score) finishedCount++
  if (value[email].cooperation.description) finishedCount++
  if (value[email].efficiency.score) finishedCount++
  if (value[email].efficiency.description) finishedCount++
  const percent = Number(((finishedCount / 6) * 100).toFixed(0))
  return percent
}

const UserSelectList: React.FC<{
  selectedUser: string
  selectUser: (email: string | string[]) => void
  removeUser: (email: string) => void
  value: UserEvaluationObj
  hasSkip: boolean
  projectUsers: ResourceUser[]
}> = ({ selectedUser, selectUser, removeUser, value, hasSkip, projectUsers }) => {
  const [users] = useAtom(usersAtom)
  const [departmentsMap] = useAtom(departmentsMapAtom)
  const [userList, setUserList] = React.useState<(ResourceUser & User)[]>([])
  const [searchUsers, setSearchUsers] = React.useState<(ResourceUser & User)[]>([])
  const [selected, setSelected] = React.useState<any>()

  React.useEffect(() => {
    if (projectUsers) {
      const dataUsers = Object.keys(value)
      const list: any[] = projectUsers
        .map((u) => ({ ...u, ...users.find((_u) => u.email === _u.email), order: dataUsers.includes(u.email) ? 1 : 0 }))
        .sort((a, b) => b.order - a.order) // 有数据的初始化到最前面

      let _userList = list.filter((l) => dataUsers.includes(l.email))
      let _searchList = list.filter((l) => !dataUsers.includes(l.email))

      if (!dataUsers.length && !hasSkip) {
        if (projectUsers.length > 5) {
          const [a, b, c, d, e, ...others] = list
          _searchList = others
          _userList = [a, b, c, d, e]
        } else {
          _userList = list
        }
      }
      setUserList(_userList)
      setSearchUsers(_searchList)
      selectUser(_userList.map((l) => l.email))
    } else {
      setUserList([])
    }
  }, [projectUsers])

  return (
    <>
      <Select
        className='w-full'
        placeholder='额外添加想要评价的成员'
        value={selected}
        onChange={(e) => {
          const user = {
            ...users.find((u) => u.email === e),
            ...projectUsers.find((u) => u.email === e),
          }
          if (user) {
            setUserList((prev) => uniqBy([user as any, ...prev], 'email'))
            selectUser(e)

            const _ = JSON.parse(JSON.stringify(searchUsers))
            _.splice(
              _.findIndex((_u: any) => _u.email === e),
              1
            )
            setSearchUsers(_)
          }
          setSelected(null)
        }}
      >
        {searchUsers.map((_u) => {
          return (
            <Select.Option key={_u.email} value={_u.email} user={_u} name={_u.name}>
              <div className='flex'>
                <UserAvatar className='mr-8px' size='small' email={_u.email} />
                <div className='text-xs leading-tight'>
                  <div>{_u.name}</div>
                  <Typography.Text type='secondary'>
                    {_u.departmentIds
                      ?.map((id) => departmentsMap![id]?.name)
                      .filter(Boolean)
                      .join(',')}
                  </Typography.Text>
                </div>
              </div>
            </Select.Option>
          )
        })}
      </Select>
      <div className='space-y-6px h-260px mt-10px overflow-y-auto'>
        {userList.map((u) => {
          const percent = calProgress(u.email, value)
          return (
            <div
              className='cursor-pointer flex space-x-4px rounded-4px py-2px px-4px items-center justify-between hover:bg-blue-400'
              key={u.email}
              onClick={() => {
                selectUser(u.email)
              }}
              style={{ background: selectedUser === u.email ? '#eee' : '' }}
            >
              <div className='flex'>
                <UserAvatar size='small' showName email={u.email} />
                <span className='mt-1px ml-8px'>{u.roles.join(',')}</span>
              </div>
              <div className='flex items-center'>
                <Progress type='circle' percent={percent} strokeWidth={14} width={36} />
                <CloseOutlined
                  className='text-xs ml-10px top-1px text-red-500 relative'
                  onClick={(e) => {
                    e.stopPropagation()
                    const _ = JSON.parse(JSON.stringify(userList))
                    const item = _.find((_u: any) => _u.email === u.email)

                    _.splice(
                      _.findIndex((_u: any) => _u.email === u.email),
                      1
                    )
                    setUserList(_)
                    removeUser(u.email)

                    setSearchUsers(searchUsers.concat([item]))
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

const getScoreColor = (score: number) => {
  const DEFAULT_BACKGROUND = '#F3F3F7'
  const DEFAULT_COLOR = '#8793A8'
  const DEFAULT_STARCOLOR = '#E3E3EC'

  let color = DEFAULT_COLOR
  let background = DEFAULT_BACKGROUND
  let starColor = DEFAULT_STARCOLOR
  if (score <= 2.5 && score >= 0.5) {
    color = '#D26018'
    background = '#FFF5E8'
    starColor = '#FF8227'
  } else if (score > 2.5 && score <= 4.5) {
    color = '#0679D1'
    background = '#E8F7FE'
    starColor = '#099DFD'
  } else if (score > 4.5) {
    color = '#3AB427'
    background = '#F0FFE8'
    starColor = '#57D73E'
  }
  return {
    background,
    color,
    starColor,
  }
}

const getScoreText = (score: number, type: number) => {
  let text = '请评分'
  switch (score) {
    case 1:
      text = type === 2 ? '经常响应很慢，甚至不响应' : '非常差'
      break
    case 2:
      text = type === 2 ? '差，有问题总是响应慢，且不太配合下游交付时间点' : '差'
      break
    case 3:
      text = type === 2 ? '一般，碰到问题有时相应' : '一般'
      break
    case 4:
      text = type === 2 ? '好，有问题积极响应' : '好'
      break
    case 5:
      text = type === 2 ? '非常好，非常积极的协助' : '非常好，超出预期'
      break
  }
  return text
}

const RateCard: React.FC<{ value: number; type: number; onChange: any }> = ({ value, onChange, type }) => {
  const [hoverScore, setHoverScore] = React.useState<number | undefined>(undefined)
  const [rateColor, setRateColor] = React.useState(getScoreColor(value).starColor)

  React.useEffect(() => {
    if (hoverScore) {
      setRateColor(getScoreColor(hoverScore).starColor)
    } else {
      setRateColor(getScoreColor(value).starColor)
    }
  }, [value, hoverScore])

  return (
    <div className='flex py-2 px-3 w-600px' style={{ background: getScoreColor(value).background }}>
      <Rate
        character={<StarFilled className='text-3xl' />}
        className='flex-shrink-0 mt-5px mr-4'
        style={{ color: rateColor }}
        value={value}
        onChange={onChange}
        onHoverChange={(value) => setHoverScore(value)}
      />
      <div>
        {!value && !hoverScore && (
          <>
            <div className='font-medium'>请打分</div>
            {!hoverScore && <div className='text-xs text-typography-body'>点击左侧进行评分</div>}
          </>
        )}
        {(value || hoverScore) && (
          <>
            <div className='font-medium'>{hoverScore ?? value}分</div>
            <div className='text-xs text-typography-body'>{getScoreText(hoverScore ?? value, type)}</div>
          </>
        )}
      </div>
    </div>
  )
}
